/* eslint-disable max-lines */
import { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import momentTZ from "moment-timezone";

import Card from "components/molecules/Card/Card";
import Hr from "components/atoms/Hr/Hr";
import UnitConverter from "components/organisms/UnitConverter/UnitConverter";
import CardNew from "components/molecules/Card/CardNew";
import { formatNumber, formatWeight } from "helpers/commonHelpers";
import useCountryNameByIsoCode from "hooks/useCountryNameByIsoCode";
import { calculateGoodItemsTotalWeight, getChargeableWeight, getDimensionalWeight } from "helpers/quotationFormHelper";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";

import nmfcClasses from "./nmfcClasses.json";

const formatStopDatetime = ({ datetime, timezone }) => {
  if (!datetime || !timezone) return null;
  return momentTZ(datetime).tz(timezone).format("DD MMM YYYY, HH:mm (UTC Z)");
};

const QuotePackingList = function ({ data, loadingData, goods_confirmation_type, organization_data }) {
  const { id } = useParams();
  const intl = useIntl();
  const getCountryName = useCountryNameByIsoCode();
  const { good_items: goodItems } = data?.organization || {};

  const { data: quoteRequestDetails } = useGetQuoteRequestDetails({
    shipmentId: id,
    options: {
      enabled: !!id,
      refetchOnMount: true,
    },
  });

  const { totalAmount, totalWeight, totalCBM, pieces } = useMemo(() => {
    let accumulateAmount = 0;
    let accumulateCBM = 0;
    let accumulatePieces = 0;
    let currencyCode = "";
    if (goodItems?.length > 0) {
      goodItems.forEach((item) => {
        accumulatePieces += item.units;
        accumulateCBM += item.total_cbm;
        if (item.good_customs_information?.length) {
          item.good_customs_information.forEach((customs_info) => {
            accumulateAmount += customs_info.value;
            if (customs_info.currency_code) currencyCode = customs_info.currency_code;
          });
        }
      });
    }
    const { weight, unit } = calculateGoodItemsTotalWeight(goodItems);
    return {
      totalAmount: `${formatNumber(accumulateAmount)} ${currencyCode}`,
      totalWeight: { weight, unit },
      pieces: accumulatePieces,
      totalCBM: accumulateCBM,
    };
  }, [goodItems]);

  const isChargeableWeightRequired =
    data?.service && (data.service.next_flight_invited || data.service.regular_air_freight_invited);

  const getStopTimeText = (stop) => {
    if (!stop) return null;

    const stopTimeType = stop?.stop_time_type;
    const afterDateTime = formatStopDatetime({
      datetime: stop?.stop_after_datetime,
      timezone: stop?.stop_timezone,
    });
    const beforeDateTime = formatStopDatetime({
      datetime: stop?.stop_before_datetime,
      timezone: stop?.stop_timezone,
    });
    const capitalize = (text = "") => (text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "");

    if (stopTimeType === "After") {
      if (!afterDateTime) return null;
      return `${capitalize(intl.formatMessage({ id: "general.after", defaultMessage: "After" }))} ${afterDateTime}`;
    }
    if (stopTimeType === "Before") {
      if (!beforeDateTime) return null;
      return `${capitalize(intl.formatMessage({ id: "general.before", defaultMessage: "Before" }))} ${beforeDateTime}`;
    }
    if (stopTimeType === "Between") {
      if (!afterDateTime || !beforeDateTime) return null;
      return `${afterDateTime} - ${beforeDateTime}`;
    }
    if (stopTimeType === "ASAP" || stopTimeType === "ASAP Delivery") {
      return intl.formatMessage({ id: "quote-request-detail.asap", defaultMessage: "ASAP" });
    }

    if (afterDateTime) return afterDateTime;
    if (beforeDateTime) return beforeDateTime;
    return null;
  };

  const getStopAddress = (stop) => {
    const streetAddress = stop?.address_details?.street_address;
    const streetAddressLineTwo = stop?.address_details?.street_address_line_two;
    if (!streetAddress && !streetAddressLineTwo) return null;
    return [streetAddress, streetAddressLineTwo].filter(Boolean).join(", ");
  };

  const getStopAirport = (stop) => {
    if (!stop || stop?.stop_address_type !== "Airport") return null;
    const airportName = stop?.airport_details?.name || stop?.address_details?.city || "";
    const airportCode = stop?.airport_details?.code || "";
    if (!airportName && !airportCode) return null;
    if (!airportCode) return airportName;
    if (!airportName) return airportCode;
    return `${airportName} (${airportCode})`;
  };

  const getStopSeaport = (stop) => {
    if (!stop || stop?.stop_address_type !== "Port") return null;
    const seaportName = stop?.seaport_details?.name || "";
    const seaportCode = stop?.seaport_details?.un_locode || "";
    if (!seaportName && !seaportCode) return null;
    if (!seaportCode) return seaportName;
    if (!seaportName) return seaportCode;
    return `${seaportName} (${seaportCode})`;
  };

  // WEB-11639: only show stop details when schedule still has >2 stops.
  const showPackingListStopDetails = (data?.schedule?.stops?.length ?? 0) > 2;

  if (goodItems?.length > 0) {
    return (
      <CardNew
        className="mb-15 font-b3"
        isLoading={loadingData}
        id="goods-div"
        title={<FormattedMessage id="general.packingList" defaultMessage="Packing list" />}
      >
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.totalPackages" defaultMessage="Total packages" />
          </Col>
          <Col>{pieces}</Col>
        </Row>
        {!isNaN(totalWeight?.weight) && (
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="quote-request-detail.totalWeight" defaultMessage="Total weight" />
            </Col>
            <Col>
              <UnitConverter
                content={<span>{formatWeight(totalWeight.weight.toFixed(2), totalWeight.unit)}</span>}
                value={totalWeight.weight.toFixed(2)}
                unit={totalWeight.unit}
              />
            </Col>
          </Row>
        )}
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="new-request.quote-confirmation.packing-list.CBM" defaultMessage="Total CBM" />
          </Col>
          <Col>{organization_data?.requested_cbm ?? parseFloat(totalCBM).toFixed(3)} m³</Col>
        </Row>
        {isChargeableWeightRequired &&
          !isNaN(totalWeight?.weight) &&
          totalWeight?.weight &&
          getChargeableWeight(totalWeight.weight, getDimensionalWeight(goodItems, totalWeight.unit)) > 0 && (
            <Row className="mb-2">
              <Col className="text-secondary">
                <FormattedMessage
                  id="new-request.quote-confirmation.packing-list.chargeableWeight"
                  defaultMessage="Total chargeable weight"
                />
              </Col>
              <Col>
                {getChargeableWeight(totalWeight.weight, getDimensionalWeight(goodItems, totalWeight.unit))}{" "}
                {totalWeight.unit}
              </Col>
            </Row>
          )}
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.totalCustomsValue" defaultMessage="Total customs value" />
          </Col>
          <Col>{totalAmount}</Col>
        </Row>
        {goods_confirmation_type && (
          <Row className="mb-2">
            <Col className="text-secondary">
              <FormattedMessage id="new-request.organisational-GoodListEstimation" defaultMessage="Goods list estimation" />
            </Col>
            <Col>{goods_confirmation_type || "-"}</Col>
          </Row>
        )}
        <Hr />
        {goodItems.length > 0 &&
          goodItems.map((item, index) => {
            // total custom amount;
            let totalCustomAmount = 0;
            const pickupStop = showPackingListStopDetails ? item?.pickup_stop : null;
            const deliveryStop = showPackingListStopDetails ? item?.delivery_stop : null;
            const pickupTime = getStopTimeText(pickupStop);
            const pickupAirport = getStopAirport(pickupStop);
            const pickupSeaport = getStopSeaport(pickupStop);
            const pickupAddress = getStopAddress(pickupStop);
            const pickupCity = pickupStop?.address_details?.city || null;
            const pickupState = pickupStop?.address_details?.state || null;
            const pickupCountry = pickupStop?.address_details?.country || null;
            const hasPickupStopDetails = Boolean(
              pickupTime || pickupAirport || pickupSeaport || pickupAddress || pickupCity || pickupState || pickupCountry,
            );

            const deliveryTime = getStopTimeText(deliveryStop);
            const deliveryAirport = getStopAirport(deliveryStop);
            const deliverySeaport = getStopSeaport(deliveryStop);
            const deliveryAddress = getStopAddress(deliveryStop);
            const deliveryCity = deliveryStop?.address_details?.city || null;
            const deliveryState = deliveryStop?.address_details?.state || null;
            const deliveryCountry = deliveryStop?.address_details?.country || null;
            const hasDeliveryStopDetails = Boolean(
              deliveryTime ||
                deliveryAirport ||
                deliverySeaport ||
                deliveryAddress ||
                deliveryCity ||
                deliveryState ||
                deliveryCountry,
            );

            return (
              <div key={`${item.handling_unit}_${Math.random()}`}>
                <div className="mb-4">
                  <FormattedMessage id="quote-request-detail.commodity" defaultMessage="Commodity" /> {index + 1}
                </div>
                {item.handling_unit && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="new-request.packing-list.handlingUnit" defaultMessage="Handling unit" />
                    </Col>
                    <Col className="text-capitalize">{`${item.handling_unit}`}</Col>
                  </Row>
                )}
                {item.container_type && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="new-request.packing-list.containerType" defaultMessage="Container Type" />
                    </Col>
                    <Col className="text-capitalize">{`${item.container_type}`}</Col>
                  </Row>
                )}
                {(item.length || item.width || item.height) && (
                  <Row className="mb-2">
                    <Col className="text-secondary">LxWxH</Col>
                    <Col className="d-flex">
                      <UnitConverter content={item.length} value={item.length} unit={item.m_unit} />
                      <span className="mx-1">x</span>
                      <UnitConverter content={item.width} value={item.width} unit={item.m_unit} />
                      <span className="mx-1">x</span>
                      <UnitConverter content={item.height} value={item.height} unit={item.m_unit} />
                      {item.m_unit}
                    </Col>
                  </Row>
                )}
                {item.other_instructions && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      {quoteRequestDetails?.organization?.good_items?.find(
                        (quoteRequestGoodItem) => quoteRequestGoodItem.handling_unit === item.handling_unit,
                      )?.additional_field_label ||
                        intl.formatMessage({
                          id: "new-request.packing-list.otherInstructions",
                          defaultMessage: "Other instructions",
                        })}
                    </Col>
                    <Col>{item.other_instructions}</Col>
                  </Row>
                )}
                <Row className="mb-2">
                  <Col className="text-secondary">
                    <FormattedMessage id="new-request.packing-list.unitWeight" defaultMessage="Unit weight" />
                  </Col>
                  <Col>
                    <UnitConverter
                      unit={item.w_unit}
                      value={item.weight?.toFixed(2)}
                      content={<span>{formatWeight(item.weight?.toFixed(2), item.w_unit)}</span>}
                    />
                  </Col>
                </Row>
                {item.units && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="new-request.packing-list.numberOfUnits" defaultMessage="Quantity" />
                    </Col>
                    <Col>{item.units}</Col>
                  </Row>
                )}

                {item.handling_unit !== "container" && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="quote-request-detail.stackable" defaultMessage="Stackable" />
                    </Col>
                    <Col>{item.stackable ? "Yes" : "No"}</Col>
                  </Row>
                )}

                {item.crating_required && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="new-request.packing-list.cratingRequired" defaultMessage="Crating required" />
                    </Col>
                    <Col>{item.crating_required ? item?.crating_required_by_date : "No"}</Col>
                  </Row>
                )}
                {typeof item.hazardous === "boolean" && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="new-request.packing-list.hazardous" defaultMessage="Hazardous" />
                    </Col>
                    <Col>{item.hazardous ? "Yes" : "No"}</Col>
                  </Row>
                )}
                {typeof item.is_dangerous_good === "boolean" && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="new-request.packing-list.dangerousGoods" defaultMessage="Dangerous goods" />
                    </Col>
                    <Col>
                      {item.is_dangerous_good
                        ? `${item?.dangerous_good_value ?? "Yes"}${item?.un_number ? ` (${item.un_number})` : ""}`
                        : "No"}
                    </Col>
                  </Row>
                )}

                {item.nmfc_class && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="new-request.packing-list.NMFC" defaultMessage="NMFC Class" />
                    </Col>
                    <Col>
                      {item.nmfc_class}
                      {item.nmfc_class && (
                        <span className="text-secondary font-weight-normal font-italic ml-2">
                          e.g. {nmfcClasses.find((c) => c.label === item.nmfc_class)?.value}
                        </span>
                      )}
                    </Col>
                  </Row>
                )}
                {item.temperature_control_required && item.temperature_control_value && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage
                        id="new-request.packing-list.temperatureControl"
                        defaultMessage="Temperature controlled"
                      />
                    </Col>
                    <Col>{(item.temperature_control_required, item.temperature_control_value || "-")}</Col>
                  </Row>
                )}
                {item.additional_information && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage
                        id="quote-request-detail.additionalInformation"
                        defaultMessage="Additional information"
                      />
                    </Col>
                    <Col>{item.additional_information || "-"}</Col>
                  </Row>
                )}
                {item.handling_required !== null && ( // boolean only in case of multi-drop shipments
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="new-request.packing-list.handlingRequired" defaultMessage="Handling required" />
                    </Col>
                    <Col>{item.handling_required ? "Yes" : "No"}</Col>
                  </Row>
                )}
                {item?.good_customs_information.length > 0 && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage
                        id="new-request.quote-confirmation.packing-list.customInformation"
                        defaultMessage="Customs information"
                      />
                    </Col>
                    <Col>
                      {item.good_customs_information.map((customs_info, i) => {
                        totalCustomAmount += customs_info.value;

                        return (
                          <div key={`${customs_info.description}_${Math.random()}`}>
                            <Card.Text>
                              {customs_info.description || ""}
                              <span className="ml-auto">
                                {`${
                                  customs_info.value
                                    ? `${formatNumber(customs_info.value)} ${customs_info.currency_code}`
                                    : ""
                                }`}
                              </span>
                            </Card.Text>

                            {customs_info.hs_code && (
                              <Card.Text>
                                <FormattedMessage id="new-request.packing-list.HsCode" defaultMessage="HS Code" />
                                <span className="ml-auto">{customs_info.hs_code}</span>
                              </Card.Text>
                            )}

                            {customs_info.origin_country && (
                              <Card.Text>
                                <FormattedMessage
                                  id="new-request.packing-list.originCountry"
                                  defaultMessage="Origin Country"
                                />
                                <span className="ml-auto">{getCountryName(customs_info.origin_country)}</span>
                              </Card.Text>
                            )}

                            <Card.Text>
                              <a href={customs_info.attachment.url} target="_blank" rel="noreferrer">
                                {customs_info.attachment.name}
                              </a>
                            </Card.Text>
                            {!!customs_info.value && (
                              <>
                                {i === item.good_customs_information.length - 1 && (
                                  <>
                                    <Hr />
                                    <Row>
                                      <Col>
                                        <FormattedMessage
                                          id="new-request.quote-confirmation.packing-list.totalCustomValue"
                                          defaultMessage="Total value"
                                        />
                                      </Col>
                                      <Col className="text-right">{`${formatNumber(totalCustomAmount)} ${
                                        customs_info.currency_code || ""
                                      }`}</Col>
                                    </Row>
                                    <Hr />
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </Col>
                  </Row>
                )}

                {hasPickupStopDetails && <div className="mt-4 mb-3" />}
                {pickupTime && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="components.molecules.top-nav.pickupTime" defaultMessage="Pickup time" />
                    </Col>
                    <Col>{pickupTime}</Col>
                  </Row>
                )}
                {pickupAirport && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="quote-request-detail.pickupAirport" defaultMessage="Pickup airport" />
                    </Col>
                    <Col>{pickupAirport}</Col>
                  </Row>
                )}
                {pickupSeaport && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="quote-request-detail.pickupSeaport" defaultMessage="Pickup seaport" />
                    </Col>
                    <Col>{pickupSeaport}</Col>
                  </Row>
                )}
                {pickupAddress && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="quote-request-detail.pickupAddress" defaultMessage="Pickup address" />
                    </Col>
                    <Col>{pickupAddress}</Col>
                  </Row>
                )}
                {pickupCity && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="general.pickupCity" defaultMessage="Pickup city" />
                    </Col>
                    <Col>{pickupCity}</Col>
                  </Row>
                )}
                {pickupState && (
                  <Row className="mb-2">
                    <Col className="text-secondary">{`${intl.formatMessage({
                      id: "general.pickup",
                      defaultMessage: "Pickup",
                    })} ${intl.formatMessage({
                      id: "general.state",
                      defaultMessage: "State",
                    })}`}</Col>
                    <Col>{pickupState}</Col>
                  </Row>
                )}
                {pickupCountry && (
                  <Row className="mb-2">
                    <Col className="text-secondary">{`${intl.formatMessage({
                      id: "general.pickup",
                      defaultMessage: "Pickup",
                    })} ${intl.formatMessage({
                      id: "general.country",
                      defaultMessage: "Country",
                    })}`}</Col>
                    <Col>{pickupCountry}</Col>
                  </Row>
                )}

                {hasDeliveryStopDetails && <div className="mt-4 mb-3" />}
                {deliveryTime && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="components.molecules.top-nav.deliveryTime" defaultMessage="Delivery time" />
                    </Col>
                    <Col>{deliveryTime}</Col>
                  </Row>
                )}
                {deliveryAirport && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="quote-request-detail.deliveryAirport" defaultMessage="Delivery airport" />
                    </Col>
                    <Col>{deliveryAirport}</Col>
                  </Row>
                )}
                {deliverySeaport && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="quote-request-detail.deliverySeaport" defaultMessage="Delivery seaport" />
                    </Col>
                    <Col>{deliverySeaport}</Col>
                  </Row>
                )}
                {deliveryAddress && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="quote-request-detail.deliveryAddress" defaultMessage="Delivery address" />
                    </Col>
                    <Col>{deliveryAddress}</Col>
                  </Row>
                )}
                {deliveryCity && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="general.deliveryCity" defaultMessage="Delivery city" />
                    </Col>
                    <Col>{deliveryCity}</Col>
                  </Row>
                )}
                {deliveryState && (
                  <Row className="mb-2">
                    <Col className="text-secondary">{`${intl.formatMessage({
                      id: "general.delivery",
                      defaultMessage: "Delivery",
                    })} ${intl.formatMessage({
                      id: "general.state",
                      defaultMessage: "State",
                    })}`}</Col>
                    <Col>{deliveryState}</Col>
                  </Row>
                )}
                {deliveryCountry && (
                  <Row className="mb-2">
                    <Col className="text-secondary">
                      <FormattedMessage id="quote-request-detail.deliveryCountry" defaultMessage="Delivery country" />
                    </Col>
                    <Col>{deliveryCountry}</Col>
                  </Row>
                )}

                {index !== goodItems.length - 1 && <Hr />}
              </div>
            );
          })}
      </CardNew>
    );
  }

  return null;
};

QuotePackingList.propTypes = {
  data: PropTypes.instanceOf(Object),
  loadingData: PropTypes.bool.isRequired,
  goods_confirmation_type: PropTypes.string,
  organization_data: PropTypes.instanceOf(Object),
};

QuotePackingList.defaultProps = {
  data: undefined,
  goods_confirmation_type: null,
  organization_data: {},
};

export default QuotePackingList;
